import React, { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Folder2,
  Microphone2,
  VoiceSquare,
  MusicSquare,
  Setting2,
  HamburgerMenu,
  Logout,
  Add,
  ArrowSwapHorizontal,
  Chart,
} from "iconsax-reactjs";
import tokenManager from "../../utils/tokenManager";
import { useUser } from "../../contexts/UserContext";
import { useLicense } from "../../hooks/useLicense";

const Sidebar = () => {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const profileRef = useRef(null);
  const { refreshUser } = useUser();
  const { hasOctopushX, accessType, loading: licenseLoading } = useLicense();

  const currentUser = tokenManager.getUser();
  const accounts = tokenManager.getAccounts();

  const OCTOPUSH_X_URL = process.env.REACT_APP_OCTOPUSH_X_URL;

  useEffect(() => {
    if (!currentUser || !tokenManager.isAuthenticated()) {
      console.warn("Invalid session detected, redirecting to login");
      tokenManager.clearCurrentSession();
      navigate("/login", { replace: true });
    }
  }, [currentUser, navigate]);

  const handleLogout = async () => {
    try {
      tokenManager.removeTokens();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const handleSwitchAccount = (userId) => {
    const success = tokenManager.switchAccount(userId);
    if (success) {
      refreshUser();
      setIsProfileDropdownOpen(false);
      window.location.reload();
    } else {
      tokenManager.clearCurrentSession();
      navigate("/login", { replace: true });
    }
  };

  const handleAddAccount = () => {
    tokenManager.removeTokens();
    navigate("/login?mode=add");
    setIsProfileDropdownOpen(false);
  };

  const handleSwitchToOctopushX = () => {
    if (OCTOPUSH_X_URL) {
      window.location.href = OCTOPUSH_X_URL;
    }
  };

  const toggleSidebar = () => {
    setIsCollapsed((prev) => !prev);
    setIsProfileDropdownOpen(false);
  };

  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen((v) => !v);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        profileRef.current &&
        !profileRef.current.contains(event.target)
      ) {
        setIsProfileDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isCollapsed) setIsProfileDropdownOpen(false);
  }, [isCollapsed]);

  if (!currentUser) return null;

  // ── Nav items ──────────────────────────────────────────────────────────
  const mainNavItems = [
    { to: "/projects", icon: Folder2, label: "All Projects" },
    { to: "/studio", icon: Microphone2, label: "Recording Studio" },
  ];

  const speechToolItems = [
    { to: "/speech", icon: VoiceSquare, label: "Speech Isolation" },
    { to: "/voice-library", icon: VoiceSquare, label: "Voice Library" },
  ];

  const musicToolItems = [
    { to: "/music-library", icon: MusicSquare, label: "Music Library" },
  ];

  const bottomNavItems = [
    { to: "/settings", icon: Setting2, label: "Settings" },
  ];

  const showCrossAppLink = !licenseLoading && hasOctopushX && !!OCTOPUSH_X_URL;

  const profileMenuItems = [
    {
      icon: Setting2,
      label: "Account settings",
      onClick: () => {
        navigate("/settings");
        setIsProfileDropdownOpen(false);
      },
    },
    ...(showCrossAppLink
      ? [
          {
            icon: ArrowSwapHorizontal,
            label: "Switch to Octopush X",
            onClick: handleSwitchToOctopushX,
          },
        ]
      : []),
  ];

  const renderNavLink = (item) => (
    <NavLink
      key={item.to}
      to={item.to}
      className={({ isActive }) =>
        `!flex !items-center !mb-0.5 ${
          isCollapsed ? "!justify-center !px-2" : "!px-3"
        } !py-2.5 ${
          isActive
            ? "!rounded-xl !text-white"
            : "!text-[#CECFD2] hover:!bg-white/[0.03]"
        } !transition-all !duration-200 !ease-in-out !rounded-xl`
      }
      style={({ isActive }) =>
        isActive
          ? {
              background: "rgba(139,92,246,0.15)",
              border: "1px solid rgba(139,92,246,0.25)",
            }
          : { border: "1px solid transparent" }
      }
      title={isCollapsed ? item.label : ""}
    >
      {({ isActive }) => (
        <>
          <div
            className={`!flex !items-center !justify-center ${
              isCollapsed ? "!w-5" : "!w-5 !mr-3"
            } !transition-all !duration-300`}
          >
            <item.icon
              className={`!h-5 !w-5 !transition-all !duration-300 ${
                isActive ? "!text-violet-400" : "!text-[#61656C]"
              }`}
            />
          </div>
          <div
            className={`!overflow-hidden !transition-all !duration-500 !ease-in-out ${
              isCollapsed ? "!w-0 !opacity-0" : "!w-auto !opacity-100"
            }`}
          >
            <span
              className={`!whitespace-nowrap !text-sm !font-medium ${
                isActive ? "!text-white" : "!text-[#CECFD2]"
              }`}
            >
              {item.label}
            </span>
          </div>
        </>
      )}
    </NavLink>
  );

  const SectionLabel = ({ label }) =>
    !isCollapsed ? (
      <p className="!text-[9px] !font-bold !text-gray-700 !uppercase !tracking-[0.12em] !px-3 !pt-4 !pb-1.5">
        {label}
      </p>
    ) : (
      <div
        className="!my-3 !mx-3 !h-px"
        style={{ background: "rgba(255,255,255,0.05)" }}
      />
    );

  return (
    <div
      className={`${
        isCollapsed ? "!w-[72px]" : "!w-72"
      } !min-h-screen !flex !flex-col !transition-all !duration-300 !ease-in-out !relative !overflow-visible`}
      style={{
        background: "linear-gradient(180deg, #0d1018 0%, #090c12 100%)",
        borderRight: "1px solid rgba(255,255,255,0.06)",
      }}
    >
      {/* Logo */}
      <div
        className={`!flex !items-center !px-4 !py-4 !transition-all !duration-300 ${
          isCollapsed ? "!justify-center" : "!justify-between"
        }`}
        style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}
      >
        <div className="!flex !items-center !overflow-hidden">
          <img
            src="/assets/logo.png"
            alt="Logo"
            className={`!h-[40px] !object-contain !transition-all !duration-300 ${
              isCollapsed ? "!scale-90" : "!scale-100"
            }`}
          />
        </div>

        {!isCollapsed && (
          <button
            onClick={toggleSidebar}
            className="!w-7 !h-7 !flex !items-center !justify-center !rounded-lg !text-gray-600 hover:!text-white !transition-colors"
            style={{ background: "rgba(255,255,255,0.04)" }}
            title="Collapse sidebar"
          >
            <HamburgerMenu className="!h-4 !w-4" />
          </button>
        )}
      </div>

      {isCollapsed && (
        <button
          onClick={toggleSidebar}
          className="!w-7 !h-7 !flex !items-center !justify-center !rounded-lg !text-gray-600 hover:!text-white !transition-colors !mx-auto !mt-3"
          style={{ background: "rgba(255,255,255,0.04)" }}
          title="Expand sidebar"
        >
          <HamburgerMenu className="!h-4 !w-4" />
        </button>
      )}

      {/* Main nav */}
      <nav className="!px-3 !pt-4 !space-y-0.5">
        {mainNavItems.map(renderNavLink)}
      </nav>

      {/* Speech tools */}
      <div className="!px-3">
        <SectionLabel label="Speech Tools" />
        <div className="!space-y-0.5">{speechToolItems.map(renderNavLink)}</div>
      </div>

      {/* Music tools */}
      <div className="!px-3">
        <SectionLabel label="Music Tools" />
        <div className="!space-y-0.5">{musicToolItems.map(renderNavLink)}</div>
      </div>

      {/* Cross-app — only if user has Octopush X access */}
      {showCrossAppLink && (
        <div className="!px-3">
          <SectionLabel label="Other Products" />
          <button
            onClick={handleSwitchToOctopushX}
            className={`!w-full !flex !items-center !mb-0.5 ${
              isCollapsed ? "!justify-center !px-2" : "!px-3"
            } !py-2.5 !text-[#CECFD2] hover:!bg-white/[0.03] !transition-all !duration-200 !ease-in-out !rounded-xl !bg-transparent !cursor-pointer`}
            style={{ border: "1px solid transparent" }}
            title={isCollapsed ? "Switch to Octopush X" : ""}
          >
            <div
              className={`!flex !items-center !justify-center ${
                isCollapsed ? "!w-5" : "!w-5 !mr-3"
              } !transition-all !duration-300`}
            >
              <Chart className="!h-5 !w-5 !text-violet-400" />
            </div>
            <div
              className={`!overflow-hidden !transition-all !duration-500 !ease-in-out ${
                isCollapsed
                  ? "!w-0 !opacity-0"
                  : "!w-auto !opacity-100 !flex !items-center !gap-2"
              }`}
            >
              <span className="!whitespace-nowrap !text-sm !font-medium !text-[#CECFD2]">
                Octopush X
              </span>
              <span className="!text-[10px] !text-[#61656C] !uppercase !tracking-wide">
                ↗
              </span>
            </div>
          </button>
        </div>
      )}

      {/* Bottom nav */}
      <div className="!mt-auto !px-3 !pb-2">
        <div
          className="!pt-2 !mb-2"
          style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}
        >
          <div className="!space-y-0.5">
            {bottomNavItems.map(renderNavLink)}
          </div>
        </div>

        {/* User profile */}
        <div
          className="!pt-2"
          style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}
        >
          <div className="!relative">
            {isCollapsed ? (
              <div
                ref={profileRef}
                onClick={toggleProfileDropdown}
                className="!flex !justify-center !cursor-pointer"
              >
                {currentUser?.profileImageUrl ? (
                  <img
                    src={currentUser.profileImageUrl}
                    alt="Profile"
                    className="!w-9 !h-9 !rounded-full hover:!opacity-90 !transition-all"
                  />
                ) : (
                  <div
                    className="!w-9 !h-9 !rounded-full !flex !items-center !justify-center !cursor-pointer !transition-all"
                    style={{ background: "rgba(139,92,246,0.2)" }}
                  >
                    <span className="!text-sm !font-bold !text-violet-300">
                      {currentUser?.name
                        ?.split(" ")
                        .map((n) => n[0])
                        .join("") || "U"}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div
                ref={profileRef}
                onClick={toggleProfileDropdown}
                className="!flex !items-center !rounded-xl !p-3 !transition-all !cursor-pointer hover:!bg-white/[0.03]"
                style={{ border: "1px solid rgba(255,255,255,0.05)" }}
              >
                {currentUser?.profileImageUrl ? (
                  <img
                    src={currentUser.profileImageUrl}
                    alt="Profile"
                    className="!w-8 !h-8 !rounded-full !mr-3"
                  />
                ) : (
                  <div
                    className="!w-8 !h-8 !rounded-full !flex !items-center !justify-center !mr-3 !flex-shrink-0"
                    style={{ background: "rgba(139,92,246,0.2)" }}
                  >
                    <span className="!text-xs !font-bold !text-violet-300">
                      {currentUser?.name
                        ?.split(" ")
                        .map((n) => n[0])
                        .join("") || "U"}
                    </span>
                  </div>
                )}
                <div className="!flex-1 !min-w-0">
                  <div className="!text-sm !font-medium !text-white !truncate">
                    {currentUser?.name || "User"}
                  </div>
                  <div className="!text-[11px] !text-gray-600 !truncate">
                    {currentUser?.email || "email@example.com"}
                  </div>
                  {/* Access badges */}
                  {!licenseLoading && accessType && (
                    <div className="!flex !gap-1 !mt-1">
                      {(accessType === "full" ||
                        accessType === "octopush_x") && (
                        <span className="!px-1.5 !py-0.5 !text-[9px] !rounded !bg-purple-600/20 !text-purple-300 !border !border-purple-700/50">
                          X
                        </span>
                      )}
                      {(accessType === "full" ||
                        accessType === "recording_studio") && (
                        <span className="!px-1.5 !py-0.5 !text-[9px] !rounded !bg-emerald-600/20 !text-emerald-300 !border !border-emerald-700/50">
                          Studio
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {isProfileDropdownOpen && (
              <div
                ref={dropdownRef}
                className={`!absolute ${
                  isCollapsed ? "!left-full !ml-2" : "!left-0 !right-0"
                } !bottom-0 !rounded-xl !shadow-2xl !overflow-hidden !z-50 ${
                  isCollapsed ? "!w-64" : "!w-full"
                }`}
                style={{
                  background:
                    "linear-gradient(180deg, #0d1018 0%, #090c12 100%)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  boxShadow: "0 16px 48px rgba(0,0,0,0.6)",
                }}
              >
                <div className="!py-2 !px-2">
                  {profileMenuItems.map((item, index) => (
                    <button
                      key={index}
                      onClick={item.onClick}
                      className="!w-full !flex !items-center !px-3 !py-2 !text-sm !text-[#CECFD2] hover:!bg-white/[0.04] !transition-colors !rounded-lg"
                    >
                      <item.icon className="!w-4 !h-4 !mr-3 !text-gray-600" />
                      {item.label}
                    </button>
                  ))}
                </div>

                <div
                  style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
                />

                <div className="!py-2">
                  <div className="!px-4 !py-1.5 !text-[10px] !font-bold !text-gray-700 !uppercase !tracking-widest">
                    Switch account
                  </div>
                  <div className="!px-2">
                    {accounts.map((account, index) => (
                      <button
                        key={index}
                        onClick={() => handleSwitchAccount(account.id)}
                        className={`!w-full !flex !items-center !px-3 !py-2 !text-sm !rounded-lg hover:!bg-white/[0.04] !transition-colors !mb-1 ${
                          account.id === currentUser?.id
                            ? "!bg-white/[0.04]"
                            : ""
                        }`}
                      >
                        {account.profileImageUrl ? (
                          <img
                            src={account.profileImageUrl}
                            alt="Account"
                            className="!w-7 !h-7 !rounded-full !mr-3"
                          />
                        ) : (
                          <div
                            className="!w-7 !h-7 !rounded-full !flex !items-center !justify-center !mr-3 !flex-shrink-0"
                            style={{ background: "rgba(139,92,246,0.2)" }}
                          >
                            <span className="!text-xs !font-bold !text-violet-300">
                              {account.name
                                ?.split(" ")
                                .map((n) => n[0])
                                .join("") || "U"}
                            </span>
                          </div>
                        )}
                        <div className="!flex-1 !text-left">
                          <div className="!text-sm !text-white !font-medium">
                            {account.name}
                          </div>
                          <div className="!text-[11px] !text-gray-600">
                            {account.email}
                          </div>
                        </div>
                        {account.id === currentUser?.id && (
                          <div className="!w-1.5 !h-1.5 !bg-violet-400 !rounded-full" />
                        )}
                      </button>
                    ))}
                  </div>
                  <div className="!px-2">
                    <button
                      onClick={handleAddAccount}
                      className="!w-full !flex !items-center !justify-center !gap-2 !px-3 !py-2 !text-sm !text-gray-500 hover:!text-white hover:!bg-white/[0.04] !transition-colors !rounded-lg"
                      style={{ border: "1px solid rgba(255,255,255,0.07)" }}
                    >
                      <Add className="!w-4 !h-4" />
                      Add account
                    </button>
                  </div>
                </div>

                <div
                  style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
                />

                <div className="!py-2 !px-2">
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsProfileDropdownOpen(false);
                    }}
                    className="!w-full !flex !items-center !px-3 !py-2 !text-sm !text-[#CECFD2] hover:!bg-white/[0.04] !transition-colors !rounded-lg"
                  >
                    <Logout className="!w-4 !h-4 !mr-3 !text-gray-600" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
